import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Calendar,
  LayoutGrid,
  ChefHat,
  Plus,
  Edit3,
  Trash2,
  Save,
  X,
  RefreshCw,
  CheckCircle,
  Upload,
  Download,
} from 'lucide-react';
import { DIAS_SEMANA, MinutaNueva, TipoPlato } from '../types/minutasNuevas';
import { generarNombreMinuta } from '../types/minutas';
import {
  MinutasNuevasAPI,
  TiposPlato,
} from '../services/minutasNuevasService';
import AyudaContextual from '../components/AyudaContextual';
import { recetasAPI, RecetaAPI } from '../services/recetasAPI';
import ConfirmModal from '../components/modals/ConfirmModal';
import { getHoyLocal } from '../utils/fechaActual';
import { useMinutasHistoricas } from '../hooks/useMinutasHistoricas';
import BuscadorMinutasHistoricasAcordeon from '../components/minutas/BuscadorMinutasHistoricasAcordeon';

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

const MinutasNuevas: React.FC = () => {
  const navigate = useNavigate();
  const [vista, setVista] = useState<'lista' | 'excel'>('lista');
  const [_minutaSeleccionada, setMinutaSeleccionada] = useState<any>(null);
  const [modoEdicion, setModoEdicion] = useState(false);

  // Estados para datos reales — `minutas` son las VIGENTES (en curso o
  // que arrancan en los próximos 30 días). Las históricas se cargan a
  // demanda desde el acordeón usando `useMinutasHistoricas`.
  const [minutas, setMinutas] = useState<MinutaNueva[]>([]);
  const [tiposPlatos, setTiposPlatos] = useState<TipoPlato[]>([]);
  const [vistaExcel, setVistaExcel] = useState<any>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const historicasHook = useMinutasHistoricas();

  // Estados para formularios
  const [mostrarModalCrear, setMostrarModalCrear] = useState(false);
  const [mostrarModalEliminar, setMostrarModalEliminar] = useState(false);
  const [minutaAEliminar, setMinutaAEliminar] = useState<number | null>(null);
  const [formularioNuevaMinuta, setFormularioNuevaMinuta] = useState({
    nombre: '',
    descripcion: '',
    fechaInicio: '',
    fechaFin: '',
    estado: 'borrador' as 'borrador' | 'activa',
  });
  const [nombrePersonalizado, setNombrePersonalizado] = useState(false); // Control para edición manual del nombre

  // Estados para selector de recetas
  const [mostrarSelectorRecetas, setMostrarSelectorRecetas] = useState(false);
  const [recetasDisponibles, setRecetasDisponibles] = useState<RecetaAPI[]>([]);
  const [cargandoRecetas, setCargandoRecetas] = useState(false);
  const [celdaSeleccionada, setCeldaSeleccionada] = useState<{
    dia: number;
    tipoPlato: TipoPlato;
  } | null>(null);

  // Estado para almacenar las recetas asignadas a cada celda
  const [recetasAsignadas, setRecetasAsignadas] = useState<{
    [key: string]: RecetaAPI; // key: "dia-tipoPlato" -> receta
  }>({});

  // Estado para mostrar indicadores de guardado
  const [_hayPendientesPorGuardar, setHayPendientesPorGuardar] = useState(false);
  const [_ultimoGuardado, setUltimoGuardado] = useState<Date | null>(null);

  // Estado para el modal de confirmación
  const [modalConfirmConfig, setModalConfirmConfig] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'warning' | 'danger' | 'info';
    onConfirm: () => void;
    confirmText?: string;
    // cancelText='' oculta el botón Cancelar (modal de solo mensaje).
    cancelText?: string;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'info',
    onConfirm: () => {},
  });

  // Cargar datos al montar el componente
  useEffect(() => {
    cargarDatos();

    // Cargar timestamp del último guardado desde localStorage
    const ultimoGuardadoStr = localStorage.getItem('minutas-ultimo-guardado');
    if (ultimoGuardadoStr) {
      try {
        const fechaGuardado = new Date(ultimoGuardadoStr);
        if (!isNaN(fechaGuardado.getTime())) {
          setUltimoGuardado(fechaGuardado);
        }
      } catch (error) {
        console.error('Error al cargar último guardado:', error);
      }
    }
  }, []);

  // Generar nombre automáticamente al seleccionar fechas (solo si no está personalizado)
  useEffect(() => {
    if (
      !nombrePersonalizado &&
      formularioNuevaMinuta.fechaInicio &&
      formularioNuevaMinuta.fechaFin
    ) {
      const nombreGenerado = generarNombreMinuta(
        'rango',
        formularioNuevaMinuta.fechaInicio,
        formularioNuevaMinuta.fechaFin
      );
      // Solo actualizar si el nombre es diferente para evitar re-renders innecesarios
      if (formularioNuevaMinuta.nombre !== nombreGenerado) {
        setFormularioNuevaMinuta((prev) => ({
          ...prev,
          nombre: nombreGenerado,
        }));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    formularioNuevaMinuta.fechaInicio,
    formularioNuevaMinuta.fechaFin,
    nombrePersonalizado,
    // Intencionalmente NO incluimos formularioNuevaMinuta.nombre para evitar loops
  ]);

  // Handlers estables con useCallback para evitar re-renders del modal
  const handleNombreChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormularioNuevaMinuta((prev) => ({
        ...prev,
        nombre: e.target.value,
      }));
    },
    []
  );

  const handleDescripcionChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setFormularioNuevaMinuta((prev) => ({
        ...prev,
        descripcion: e.target.value,
      }));
    },
    []
  );

  const handleFechaInicioChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormularioNuevaMinuta((prev) => ({
        ...prev,
        fechaInicio: e.target.value,
      }));
    },
    []
  );

  const handleFechaFinChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setFormularioNuevaMinuta((prev) => ({
        ...prev,
        fechaFin: e.target.value,
      }));
    },
    []
  );

  const handlePersonalizadoChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setNombrePersonalizado(checked);
      // Si se desmarca, regenerar nombre automático
      if (!checked) {
        setFormularioNuevaMinuta((prev) => {
          if (prev.fechaInicio && prev.fechaFin) {
            const nombreGenerado = generarNombreMinuta(
              'rango',
              prev.fechaInicio,
              prev.fechaFin
            );
            return {
              ...prev,
              nombre: nombreGenerado,
            };
          }
          return prev;
        });
      }
    },
    []
  );

  const handleEstadoChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setFormularioNuevaMinuta((prev) => ({
        ...prev,
        estado: e.target.value as 'borrador' | 'activa',
      }));
    },
    []
  );

  const cargarDatos = async () => {
    setCargando(true);
    setError(null);

    try {
      console.log('🔄 Iniciando carga de datos...');

      // Cargar tipos de platos y minutas vigentes en paralelo.
      // Default operativo: minutas en curso o que arrancan en los
      // próximos 30 días, SIN filtro de estado — el equipo necesita
      // ver borradores y completadas vigentes (no sólo activas). Las
      // canceladas se descartan abajo: una cancelada vigente no debe
      // contaminar la vista operativa; quien la necesite la encuentra
      // en el acordeón histórico.
      const [tiposPlatosResponse, minutasResponse] = await Promise.all([
        TiposPlato.obtenerTodos(),
        MinutasNuevasAPI.listar({
          limit: 50,
          vigentes: true,
          hoy: getHoyLocal(),
        }),
      ]);

      if (tiposPlatosResponse.success) {
        setTiposPlatos(tiposPlatosResponse.data);
        console.log(
          '✅ Tipos de platos cargados:',
          tiposPlatosResponse.data.length
        );
      }

      if (minutasResponse.success) {
        console.log('✅ Minutas cargadas:', minutasResponse.data.length);

        // Adaptar minutas para incluir configuracionFilas y fechas
        // correctas. Las canceladas vigentes se descartan del grid
        // principal (decisión #13-#14 de la matriz); siguen siendo
        // accesibles vía acordeón histórico cuando el usuario las
        // busca explícitamente por rango de fecha.
        const minutasAdaptadas = minutasResponse.data
          .filter((minuta: any) => minuta.estado !== 'cancelada')
          .map((minuta: any) => ({
          ...minuta,
          // Mapear fechas desde snake_case a camelCase
          fechaInicio: new Date(minuta.fecha_inicio || minuta.fechaInicio),
          fechaFin: new Date(minuta.fecha_fin || minuta.fechaFin),
          // Mapear configuración de filas
          configuracionFilas: minuta.configuracion_filas || {
            Desayuno: 1,
            Ensalada: 1,
            ColacionPrincipal: 1,
            Hipocalorico: 1,
            Almuerzo: 1,
            OnceCompleta: 1,
            Bebida: 1,
            Cena: 1,
            ColacionNocturna: 1,
            Postre: 1,
          },
        }));

        console.log('📊 Estadísticas de minutas:');
        console.log('📋 Datos originales de la API:', minutasResponse.data[0]);

        minutasAdaptadas.forEach((minuta, index) => {
          console.log(`  Minuta ${index + 1} (${minuta.nombre}):`);
          console.log(
            `    - Total recetas: ${minuta.estadisticas?.totalRecetas || 0}`
          );
          console.log(
            `    - Completitud del servidor: ${minuta.estadisticas?.completitudPorcentaje || 0}%`
          );
          console.log(`    - Configuración filas:`, minuta.configuracionFilas);
          console.log(`    - Fecha inicio adaptada:`, minuta.fechaInicio);
          console.log(`    - Fecha fin adaptada:`, minuta.fechaFin);

          // Calcular días reales para verificar
          const fechaInicio = new Date(minuta.fechaInicio);
          const fechaFin = new Date(minuta.fechaFin);
          const diasCalculados =
            Math.ceil(
              (fechaFin.getTime() - fechaInicio.getTime()) /
                (1000 * 60 * 60 * 24)
            ) + 1;
          console.log(`    - Días calculados: ${diasCalculados}`);

          let totalFilas = 0;
          const config = minuta.configuracionFilas || {};
          for (const [_tipo, count] of Object.entries(config)) {
            // Solo contar filas activas (valor > 0)
            if (count && (count as number) > 0) {
              totalFilas += count as number;
            }
          }
          console.log(`    - Total filas configuradas: ${totalFilas}`);
          console.log(
            `    - Celdas esperadas (correcto): ${diasCalculados} × ${totalFilas} = ${diasCalculados * totalFilas}`
          );
          console.log(
            `    - Porcentaje correcto: ${minuta.estadisticas?.totalRecetas || 0}/${diasCalculados * totalFilas} = ${Math.round(((minuta.estadisticas?.totalRecetas || 0) / (diasCalculados * totalFilas)) * 100)}%`
          );
        });
        setMinutas(minutasAdaptadas);
      }
    } catch (err) {
      console.error('Error al cargar datos:', err);
      setError('Error al cargar los datos. Por favor, intenta nuevamente.');
    } finally {
      setCargando(false);
    }
  };

  // Fix tanda2 T2-05 (2026-06-30): la creación puede fallar con 409
  // SOLAPE_DE_MINUTAS si el rango se solapa con otra activa. En ese
  // caso mostramos un modal de confirmación explícita ("Ya existe
  // 'X' entre YYYY-MM-DD y ZZZZ-MM-DD. ¿Crear de todos modos?"). Si
  // el usuario confirma, reenviamos con forzarSolape=true.
  const enviarCrearMinuta = useCallback(
    async (forzarSolape: boolean = false) => {
      setCargando(true);
      setError(null);
      try {
        const nuevaMinuta = await MinutasNuevasAPI.crear({
          nombre: formularioNuevaMinuta.nombre,
          descripcion: formularioNuevaMinuta.descripcion,
          fechaInicio: formularioNuevaMinuta.fechaInicio,
          fechaFin: formularioNuevaMinuta.fechaFin,
          estado: formularioNuevaMinuta.estado,
          forzarSolape,
        });
        if (nuevaMinuta.success) {
          setMinutas((prev) => [nuevaMinuta.data, ...prev]);
          setFormularioNuevaMinuta({
            nombre: '',
            descripcion: '',
            fechaInicio: '',
            fechaFin: '',
            estado: 'borrador',
          });
          setNombrePersonalizado(false);
          setMostrarModalCrear(false);
        }
      } catch (err: unknown) {
        const axiosErr = err as {
          response?: { status?: number; data?: { error?: string; solapes?: Array<{ nombre: string; fecha_inicio: string; fecha_fin: string }>; message?: string } };
        };
        const status = axiosErr?.response?.status;
        const backendError = axiosErr?.response?.data?.error;
        if (status === 409 && backendError === 'SOLAPE_DE_MINUTAS' && !forzarSolape) {
          const solapes = axiosErr.response?.data?.solapes || [];
          const detalles = solapes
            .map((s) => `• "${s.nombre}" (${s.fecha_inicio} → ${s.fecha_fin})`)
            .join('\n');
          setModalConfirmConfig({
            isOpen: true,
            title: 'El rango se solapa con otra minuta',
            message: `Ya existe ${solapes.length} minuta${solapes.length === 1 ? '' : 's'} activa${solapes.length === 1 ? '' : 's'} en ese rango:\n${detalles}\n\n¿Crear la nueva minuta de todos modos?`,
            type: 'warning',
            confirmText: 'Crear igual',
            cancelText: 'Cancelar',
            onConfirm: () => enviarCrearMinuta(true),
          });
          return;
        }
        console.error('Error al crear minuta:', err);
        setError('Error al crear la minuta. Por favor, intenta nuevamente.');
      } finally {
        setCargando(false);
      }
    },
    [formularioNuevaMinuta]
  );

  const manejarCrearMinuta = useCallback(async () => {
    await enviarCrearMinuta(false);
  }, [enviarCrearMinuta]);

  const obtenerColorEstado = (estado: string) => {
    const colores = {
      borrador: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300',
      activa:
        'bg-green-100 text-green-800 dark:bg-green-700 dark:text-green-300',
      completada:
        'bg-blue-100 text-blue-800 dark:bg-blue-700 dark:text-blue-300',
      cancelada: 'bg-red-100 text-red-800 dark:bg-red-700 dark:text-red-300',
    };
    return colores[estado as keyof typeof colores] || colores.borrador;
  };

  const formatearMonto = (monto: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
    }).format(monto);
  };

  // Función para calcular el costo real de una receta basado en sus ingredientes
  const calcularCostoReal = (receta: RecetaAPI): number => {
    if (!receta.ingredientes || receta.ingredientes.length === 0) {
      return 0;
    }

    return receta.ingredientes.reduce((total, ingredienteReceta) => {
      const costoParcial =
        typeof ingredienteReceta.costo_parcial === 'number'
          ? ingredienteReceta.costo_parcial
          : parseFloat(ingredienteReceta.costo_parcial || '0');
      return total + costoParcial;
    }, 0);
  };

  // Función para calcular el total de costos por día
  const calcularTotalPorDia = (dia: number): number => {
    let total = 0;

    tiposPlatos.forEach((tipoPlato) => {
      const clavecelda = `${dia}-${tipoPlato.id}`;
      const recetaLocal = recetasAsignadas[clavecelda];
      const recetaServidor = vistaExcel?.semana?.[dia]?.[tipoPlato.id];
      const recetaAsignada = recetaLocal || recetaServidor;

      if (recetaAsignada) {
        const costo = recetaLocal
          ? calcularCostoReal(recetaLocal)
          : recetaServidor?.receta
            ? calcularCostoReal(recetaServidor.receta)
            : 0;
        total += costo;
      }
    });

    return total;
  };

  // Función para calcular estadísticas correctas para una minuta específica.
  //
  // 2026-06-16 — definición final (Jorge):
  //   - Casilla COMPLETA = tiene receta asignada O está marcada "No aplica".
  //     ("No aplica" es una forma válida de completar la casilla.)
  //   - Casilla PENDIENTE = no tiene receta Y no está marcada "No aplica".
  //   - Completitud % = celdasCompletas / celdasTotales (absoluto). Cap 100%.
  //   - La barra muestra "completas / total" usando EXACTAMENTE el mismo
  //     numerador y denominador que el %. Antes la barra mostraba "83/98
  //     recetas" mientras el % usaba un denominador distinto → divergían.
  //   - El número "83 Recetas" queda como conteo INFORMATIVO, no como
  //     fracción de avance.
  //
  // Historial previo: el frontend recalculaba mal el denominador y daba
  // 169% imposible (sesión #024); luego se confió en el backend pero el
  // numerador no incluía las omitidas. Ahora completas = recetas + omitidas
  // sobre el total absoluto (sin descontar omitidas del denominador).
  const calcularEstadisticasCorrectas = (minuta: MinutaNueva) => {
    const fechaInicio = new Date(minuta.fechaInicio);
    const fechaFin = new Date(minuta.fechaFin);

    if (isNaN(fechaInicio.getTime()) || isNaN(fechaFin.getTime())) {
      return null;
    }

    const diasReales =
      Math.ceil(
        (fechaFin.getTime() - fechaInicio.getTime()) / (1000 * 60 * 60 * 24)
      ) + 1;

    const totalRecetas = minuta.estadisticas?.totalRecetas || 0;
    const celdasTotalesBackend =
      (minuta.estadisticas as { celdasTotales?: number } | null | undefined)
        ?.celdasTotales || 0;
    const celdasOmitidas =
      (minuta.estadisticas as { celdasOmitidas?: number } | null | undefined)
        ?.celdasOmitidas || 0;

    // Fallback al cálculo local solo si el backend no envió celdasTotales.
    let totalCeldasPosibles = celdasTotalesBackend;
    let totalFilas = 0;
    if (!totalCeldasPosibles) {
      const config = minuta.configuracionFilas || {};
      for (const [_tipo, count] of Object.entries(config)) {
        if (count && (count as number) > 0) totalFilas += count as number;
      }
      totalCeldasPosibles = diasReales * totalFilas;
    }

    // Nueva métrica de completitud (definición Jorge 2026-06-16):
    //   completas = recetas asignadas + "No aplica"
    //   total = total absoluto de casillas (NO descontar omitidas)
    const celdasCompletas = totalRecetas + celdasOmitidas;
    const totalAbsoluto = totalCeldasPosibles;
    const crudo =
      totalAbsoluto > 0
        ? Math.round((celdasCompletas / totalAbsoluto) * 100)
        : celdasCompletas > 0
          ? 100
          : 0;
    const completitudPorcentaje = Math.min(100, Math.max(0, crudo));

    return {
      totalRecetas, // conteo informativo de recetas (no fracción)
      completitudPorcentaje,
      totalCeldasPosibles,
      celdasOmitidas,
      celdasCompletas, // numerador del %
      totalAbsoluto, // denominador del %
      diasReales,
      totalFilas,
    };
  };

  // Función para calcular estadísticas en tiempo real de la vista Excel
  const calcularEstadisticasEnTiempoReal = () => {
    if (!vistaExcel) return null;

    let totalRecetas = 0;
    let diasConRecetas = 0;
    let costoTotal = 0;
    const recetasPorDia: { [dia: number]: number } = {};

    // Contar recetas por día
    for (let dia = 1; dia <= 7; dia++) {
      let recetasEnDia = 0;
      let costoDelDia = 0;

      tiposPlatos.forEach((tipoPlato) => {
        const clavecelda = `${dia}-${tipoPlato.id}`;
        const recetaLocal = recetasAsignadas[clavecelda];
        const recetaServidor = vistaExcel.semana?.[dia]?.[tipoPlato.id];
        const recetaAsignada = recetaLocal || recetaServidor;

        if (recetaAsignada) {
          recetasEnDia++;
          totalRecetas++;

          const costo = recetaLocal
            ? calcularCostoReal(recetaLocal)
            : recetaServidor?.receta
              ? calcularCostoReal(recetaServidor.receta)
              : 0;
          costoDelDia += costo;
        }
      });

      recetasPorDia[dia] = recetasEnDia;
      costoTotal += costoDelDia;

      if (recetasEnDia > 0) {
        diasConRecetas++;
      }
    }

    // Calcular celdas totales basándose en la configuración real de filas por tipo y días reales
    const minutaActual = minutas.find(
      (m) => vistaExcel?.minuta?.id && m.id === vistaExcel.minuta.id
    );
    const configuracionFilas = minutaActual?.configuracionFilas || {};

    // Calcular total de filas basándose en la configuración (solo tipos activos, count > 0)
    const totalFilas = Object.values(configuracionFilas).reduce(
      (sum, count) => {
        const numCount = (count as number) || 0;
        return numCount > 0 ? sum + numCount : sum;
      },
      0
    );

    // Calcular días reales de la minuta
    let diasReales = 7; // fallback
    if (minutaActual) {
      const fechaInicio = new Date(minutaActual.fechaInicio);
      const fechaFin = new Date(minutaActual.fechaFin);
      if (!isNaN(fechaInicio.getTime()) && !isNaN(fechaFin.getTime())) {
        diasReales =
          Math.ceil(
            (fechaFin.getTime() - fechaInicio.getTime()) / (1000 * 60 * 60 * 24)
          ) + 1;
      }
    }

    const totalCeldasPosibles = diasReales * totalFilas;
    const completitudPorcentaje = Math.round(
      (totalRecetas / totalCeldasPosibles) * 100
    );

    return {
      totalRecetas,
      diasConRecetas,
      costoTotal,
      completitudPorcentaje,
      recetasPorDia,
      totalCeldasPosibles, // Agregar para debug
      diasReales, // Agregar para debug
      totalFilas, // Agregar para debug
    };
  };

  // Memorizar las estadísticas en tiempo real para evitar recálculos innecesarios
  const estadisticasEnTiempoReal = useMemo(() => {
    const stats = calcularEstadisticasEnTiempoReal();
    if (stats) {
      console.log('📊 Estadísticas recalculadas en tiempo real:', {
        totalRecetas: stats.totalRecetas,
        diasReales: stats.diasReales,
        totalFilas: stats.totalFilas,
        totalCeldasPosibles: stats.totalCeldasPosibles,
        completitudPorcentaje: stats.completitudPorcentaje,
        calculo: `${stats.totalRecetas}/${stats.totalCeldasPosibles} = ${stats.completitudPorcentaje}%`,
        formula: `${stats.diasReales} días × ${stats.totalFilas} filas = ${stats.totalCeldasPosibles} celdas`,
      });
    }
    return stats;
  }, [vistaExcel, recetasAsignadas, tiposPlatos, minutas]);
  // Función simple para formatear fechas sin conversiones adicionales
  const formatearFechaSimple = (fecha: any) => {
    if (!fecha) return 'Fecha no disponible';

    try {
      let fechaObj: Date;

      if (fecha instanceof Date) {
        fechaObj = fecha;
      } else if (typeof fecha === 'string') {
        // Las fechas vienen como "2025-09-29T00:00:00.000Z" desde la API
        // Extraer solo la parte de la fecha (YYYY-MM-DD) para evitar conversión UTC
        const soloFecha = fecha.split('T')[0]; // "2025-09-29"
        // Crear fecha local sin conversión UTC
        const [año, mes, dia] = soloFecha.split('-').map(Number);
        fechaObj = new Date(año, mes - 1, dia); // mes - 1 porque Date usa base 0 para meses
      } else {
        return 'Formato de fecha no válido';
      }

      if (isNaN(fechaObj.getTime())) return 'Fecha inválida';

      return fechaObj.toLocaleDateString('es-CL', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
      });
    } catch (error) {
      console.error('Error formateando fecha:', fecha, error);
      return 'Error en fecha';
    }
  };

  const cargarVistaExcel = async (minutaId: number) => {
    setCargando(true);
    setError(null);

    try {
      const response = await MinutasNuevasAPI.obtenerVistaExcel(minutaId);

      if (response.success) {
        setVistaExcel(response.data);
      } else {
        setError('Error al cargar la vista Excel');
      }
    } catch (err) {
      console.error('Error al cargar vista Excel:', err);
      setError(
        'Error al cargar la vista Excel. Por favor, intenta nuevamente.'
      );
    } finally {
      setCargando(false);
    }
  };

  const abrirSelectorRecetas = async (dia: number, tipoPlato: TipoPlato) => {
    setCeldaSeleccionada({ dia, tipoPlato });
    setCargandoRecetas(true);
    setMostrarSelectorRecetas(true);

    try {
      // El nombre del tipo de plato en la tabla tipos_plato
      console.log('🔍 Objeto tipoPlato completo:', tipoPlato);
      console.log('🔍 tipoPlato.id:', tipoPlato.id);
      console.log('🔍 tipoPlato.nombre:', tipoPlato.nombre);

      const nombreTipoPlato = tipoPlato.nombre;

      console.log('🔍 Buscando recetas para tipo de plato:', nombreTipoPlato);

      // Llamar al API filtrando directamente por tipoPlato
      // El campo tipo_plato en recetas es VARCHAR y contiene valores como:
      // 'Desayuno', 'Almuerzo', 'Cena', 'Ensalada', 'Bebida', 'Postre',
      // 'Colación Principal', 'Colación Fría', 'Hipocalórico', 'Dieta Blanda', etc.
      const response = await recetasAPI.listar({
        tipoPlato: nombreTipoPlato,
        activo: true,
        limit: 100,
      });

      console.log(
        `✅ Recetas encontradas para "${nombreTipoPlato}":`,
        response.data?.length || 0
      );

      if (response.data && response.data.length > 0) {
        console.log(
          '- Primeras 3 recetas:',
          response.data.slice(0, 3).map((r: any) => r.nombre)
        );
      }

      setRecetasDisponibles(response.data || []);
    } catch (err) {
      console.error('Error al cargar recetas:', err);
      setError('Error al cargar las recetas disponibles');
      setRecetasDisponibles([]);
    } finally {
      setCargandoRecetas(false);
    }
  };

  const seleccionarReceta = async (receta: RecetaAPI) => {
    if (!celdaSeleccionada) return;

    // Mostrar feedback inmediato en la UI
    const clavecelda = `${celdaSeleccionada.dia}-${celdaSeleccionada.tipoPlato.id}`;
    setRecetasAsignadas((prev) => ({
      ...prev,
      [clavecelda]: receta,
    }));

    console.log(
      '✅ Receta asignada localmente:',
      receta.nombre,
      'para',
      celdaSeleccionada.tipoPlato.nombre,
      'día',
      celdaSeleccionada.dia
    );

    // Cerrar modal
    setMostrarSelectorRecetas(false);
    const diaSeleccionado = celdaSeleccionada.dia;
    const tipoPlatoSeleccionado = celdaSeleccionada.tipoPlato;
    setCeldaSeleccionada(null);

    // Guardar automáticamente en el backend (esto recargará los datos)
    await guardarCambioAutomatico(
      diaSeleccionado,
      tipoPlatoSeleccionado,
      receta
    );
  };

  // Función para guardar un cambio individual automáticamente
  const guardarCambioAutomatico = async (
    dia: number,
    tipoPlato: TipoPlato,
    receta: RecetaAPI | null = null
  ) => {
    if (!vistaExcel) return;

    console.log('🔄 Iniciando guardado automático:', {
      dia,
      tipoPlato: tipoPlato.nombre,
      receta: receta?.nombre,
      fechaInicio: vistaExcel.minuta.fechaInicio,
      fechaInicioType: typeof vistaExcel.minuta.fechaInicio,
    });

    try {
      // Verificar si hay una asignación existente y eliminarla primero
      const asignacionExistente = vistaExcel.semana?.[dia]?.[tipoPlato.id];
      if (asignacionExistente && asignacionExistente.id) {
        // Usar el endpoint canónico /minutas-nuevas/:id/remover-receta/:detalleId.
        // Antes apuntaba a MinutasDetalladasAPI.eliminar, que llamaba al stub
        // 501 DELETE /minutas-detalladas/:id y la asignación nunca se borraba.
        await MinutasNuevasAPI.removerRecetaMinuta(
          vistaExcel.minuta.id,
          asignacionExistente.id
        );
        console.log(
          '🔄 Eliminada asignación anterior para',
          tipoPlato.nombre,
          'día',
          dia
        );
      }

      if (receta) {
        // Asignar nueva receta
        // Manejar correctamente el parsing de la fecha
        let fechaInicio: Date;

        // Convertir la fecha de inicio desde diferentes formatos posibles
        const fechaInicioRaw = vistaExcel.minuta.fechaInicio;

        if (fechaInicioRaw instanceof Date) {
          fechaInicio = fechaInicioRaw;
        } else if (typeof fechaInicioRaw === 'string') {
          // Intentar parsear diferentes formatos de fecha string
          fechaInicio = new Date(fechaInicioRaw);

          // Si el parseo directo falla, intentar con formato YYYY-MM-DD
          if (
            isNaN(fechaInicio.getTime()) &&
            fechaInicioRaw.match(/^\d{4}-\d{2}-\d{2}/)
          ) {
            fechaInicio = new Date(fechaInicioRaw + 'T00:00:00.000Z');
          }
        } else {
          console.error(
            '❌ Formato de fecha no soportado:',
            fechaInicioRaw,
            typeof fechaInicioRaw
          );
          return;
        }

        // Verificar que la fecha sea válida
        if (isNaN(fechaInicio.getTime())) {
          console.error(
            '❌ Fecha inválida después del parseo:',
            fechaInicioRaw
          );
          // Usar fecha actual como fallback
          fechaInicio = new Date();
          console.log('🔄 Usando fecha actual como fallback:', fechaInicio);
        }

        console.log(
          '✅ Fecha procesada correctamente:',
          fechaInicio.toISOString()
        );

        const fechaEspecifica = new Date(fechaInicio);
        fechaEspecifica.setDate(fechaInicio.getDate() + dia - 1);

        const fechaEspecificaStr = fechaEspecifica.toISOString().split('T')[0];

        console.log('📅 Fecha específica calculada:', fechaEspecificaStr);

        // Endpoint canónico /minutas-nuevas/:id/asignar-receta. Hace UPSERT
        // sobre la celda (minuta + fecha + tipo + fila): UPDATE si ya existe,
        // INSERT si no. Antes esto pegaba al stub 501 POST /minutas-detalladas
        // y la asignación nunca persistía (el response del frontend mentía).
        const datosAsignacion = {
          fecha_especifica: fechaEspecificaStr,
          tipo_plato_id: tipoPlato.id,
          receta_id: receta.id,
          fila_orden: (tipoPlato as any).fila_orden || 1,
        };

        console.log('📤 Enviando datos de asignación:', datosAsignacion);

        await MinutasNuevasAPI.asignarRecetaMinuta(
          vistaExcel.minuta.id,
          datosAsignacion
        );
        console.log('✅ Receta guardada automáticamente:', receta.nombre);
      } else {
        console.log(
          '✅ Receta eliminada automáticamente para',
          tipoPlato.nombre,
          'día',
          dia
        );
      }

      // Recargar vista Excel para reflejar cambios del servidor
      await cargarVistaExcel(vistaExcel.minuta.id);

      // Recargar lista de minutas para actualizar estadísticas en el resumen
      console.log(
        '🔄 Recargando lista de minutas para actualizar estadísticas...'
      );
      await cargarDatos();
      console.log('✅ Lista de minutas recargada');

      // Limpiar estado de recetas asignadas localmente ya que se recargan del servidor
      setRecetasAsignadas({});
      setHayPendientesPorGuardar(false);
    } catch (error) {
      console.error('❌ Error al guardar cambio automático:', error);
    }
  };

  // Función para eliminar una receta asignada
  const eliminarReceta = async (dia: number, tipoPlato: TipoPlato) => {
    // Mostrar feedback inmediato en la UI
    const clavecelda = `${dia}-${tipoPlato.id}`;
    setRecetasAsignadas((prev) => {
      const newState = { ...prev };
      delete newState[clavecelda];
      return newState;
    });

    console.log(
      '🗑️ Receta eliminada localmente para',
      tipoPlato.nombre,
      'día',
      dia
    );

    // Guardar automáticamente la eliminación en el backend (esto recargará los datos)
    await guardarCambioAutomatico(dia, tipoPlato, null);
  };

  // Función para mostrar el modal de confirmación de eliminación
  const eliminarMinuta = (minutaId: number) => {
    // Encontrar la minuta para mostrar su nombre en el modal
    const minuta = minutas.find((m) => m.id === minutaId);
    if (minuta) {
      setMinutaSeleccionada(minuta);
    }
    setMinutaAEliminar(minutaId);
    setMostrarModalEliminar(true);
  };

  // Función para confirmar la eliminación de la minuta
  const confirmarEliminarMinuta = async () => {
    if (!minutaAEliminar) return;

    try {
      setCargando(true);
      await MinutasNuevasAPI.eliminar(minutaAEliminar);

      // Recargar la lista de minutas
      await cargarDatos();

      // Si estamos en vista excel de esa minuta, volver a vista lista
      if (vistaExcel && vistaExcel.minuta.id === minutaAEliminar) {
        setVista('lista');
        setVistaExcel(null);
      }

      // Cerrar el modal
      setMostrarModalEliminar(false);
      setMinutaAEliminar(null);
    } catch (error) {
      console.error('Error eliminando minuta:', error);
      setError('Error al eliminar la minuta');
    } finally {
      setCargando(false);
    }
  };

  // Función para publicar una minuta (cambiar estado a 'activa').
  // BLOQUEA si la minuta tiene celdas sin decidir (sin receta y sin
  // "No aplica"). El chef debe completarlas o marcarlas explícitamente
  // como "No aplica" antes de publicar. El backend valida lo mismo (409).
  const publicarMinuta = async (
    minutaId: number,
    celdasFaltantes: number = 0
  ) => {
    if (celdasFaltantes > 0) {
      setModalConfirmConfig({
        isOpen: true,
        title: 'No se puede publicar',
        message: `Faltan ${celdasFaltantes} celdas sin decidir (sin receta y sin "No aplica"). Abrí el editor para completarlas o marcarlas como "No aplica" antes de publicar.`,
        type: 'warning',
        confirmText: 'Entendido',
        cancelText: '',
        onConfirm: () => {},
      });
      return;
    }

    setModalConfirmConfig({
      isOpen: true,
      title: 'Publicar Minuta',
      message:
        '¿Deseas publicar esta minuta? Esto la pondrá disponible para uso.',
      type: 'info',
      confirmText: 'Publicar',
      cancelText: 'Cancelar',
      onConfirm: async () => {
        try {
          setCargando(true);

          const response = await MinutasNuevasAPI.actualizarEstado(
            minutaId,
            'activa'
          );

          if (response.success) {
            console.log('✅ Minuta publicada:', response);
            // Recargar la lista para reflejar el cambio de estado.
            await cargarDatos();
          } else {
            throw new Error('Error al publicar minuta');
          }
        } catch (error: any) {
          console.error('❌ Error publicando minuta:', error);

          // El backend valida completitud y devuelve 409 si faltan celdas.
          // Defensa en profundidad: si el frontend tenía estadísticas
          // desactualizadas, el backend rechaza y mostramos el mensaje.
          const status = error?.response?.status;
          const backendError = error?.response?.data?.error;
          const esBloqueoCompletitud = status === 409;

          setError(backendError || 'Error al publicar la minuta');
          setModalConfirmConfig({
            isOpen: true,
            title: esBloqueoCompletitud
              ? 'No se puede publicar'
              : 'Error',
            message:
              backendError ||
              'No se pudo publicar la minuta. Intenta nuevamente.',
            type: esBloqueoCompletitud ? 'warning' : 'danger',
            confirmText: 'Entendido',
            cancelText: '',
            onConfirm: () => {},
          });
        } finally {
          setCargando(false);
        }
      },
    });
  };

  // Función para bajar una minuta (cambiar estado de 'activa' a 'borrador')
  const bajarMinuta = async (minutaId: number) => {
    setModalConfirmConfig({
      isOpen: true,
      title: 'Bajar Minuta',
      message:
        '¿Deseas bajar esta minuta a borrador? Dejará de estar disponible para uso.',
      type: 'warning',
      onConfirm: async () => {
        try {
          setCargando(true);

          // Llamar al API para cambiar el estado a 'borrador'
          const response = await MinutasNuevasAPI.actualizarEstado(
            minutaId,
            'borrador'
          );

          if (response.success) {
            console.log('✅ Minuta bajada a borrador:', response);

            // Mostrar mensaje de éxito con el modal
            setModalConfirmConfig({
              isOpen: true,
              title: 'Éxito',
              message: 'Minuta bajada a borrador exitosamente',
              type: 'info',
              onConfirm: async () => {
                // Recargar la lista para reflejar el cambio de estado
                await cargarDatos();
              },
            });
          } else {
            throw new Error('Error al bajar minuta');
          }
        } catch (error) {
          console.error('❌ Error bajando minuta:', error);
          setError('Error al bajar la minuta');
          setModalConfirmConfig({
            isOpen: true,
            title: 'Error',
            message: 'No se pudo bajar la minuta. Intenta nuevamente.',
            type: 'danger',
            onConfirm: () => {},
          });
        } finally {
          setCargando(false);
        }
      },
    });
  };

  // ============================================================================
  // VISTA LISTA DE MINUTAS
  // ============================================================================

  const VistaLista = () => {
    // Mostrar indicador de carga
    if (cargando) {
      return (
        <div className="flex justify-center items-center py-20">
          <div className="text-lg text-gray-600 dark:text-gray-400">
            Cargando minutas...
          </div>
        </div>
      );
    }

    // Mostrar error
    if (error) {
      return (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
          <div className="text-red-800 dark:text-red-200 font-medium">
            Error al cargar datos
          </div>
          <div className="text-red-600 dark:text-red-400 text-sm mt-1">
            {error}
          </div>
          <button
            onClick={cargarDatos}
            className="mt-3 px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Reintentar
          </button>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {/* Header con botones */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center gap-2">
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Minutas Nuevas
              </h1>
              <AyudaContextual seccion="minutas" />
            </div>
            <p className="text-gray-600 dark:text-gray-400">
              Sistema avanzado con tipos específicos de platos por día
            </p>
          </div>
          <div className="flex space-x-3">
            <button
              onClick={() => setMostrarModalCrear(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center space-x-2"
            >
              <Plus className="h-4 w-4" />
              <span>Nueva Minuta</span>
            </button>
            <button className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center space-x-2">
              <LayoutGrid className="h-4 w-4" />
              <span>Plantilla Completa</span>
            </button>
          </div>
        </div>

        {/* Aviso si no hay minutas vigentes — Manuel sigue viendo el
            acordeón abajo para buscar históricas. */}
        {minutas.length === 0 && !cargando && (
          <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4 text-amber-800 dark:text-amber-200 text-sm">
            No hay minutas disponibles para los próximos 30 días.
            Probá el buscador de minutas anteriores abajo.
          </div>
        )}

        {/* Cards de minutas */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {minutas.map((minuta) => (
            <div
              key={minuta.id}
              className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6"
            >
              {/* Header de la card */}
              <div className="flex justify-between items-start mb-4">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                    {minuta.nombre}
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400">
                    {minuta.descripcion}
                  </p>
                </div>
                <div className="flex space-x-2">
                  <span
                    className={`px-2 py-1 rounded-full text-xs font-medium ${obtenerColorEstado(minuta.estado)}`}
                  >
                    {minuta.estado}
                  </span>
                </div>
              </div>

              {/* Fechas */}
              <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600 dark:text-gray-400">
                <div className="flex items-center space-x-1">
                  <Calendar className="h-4 w-4" />
                  <span>
                    {formatearFechaSimple(
                      (minuta as any).fecha_inicio || minuta.fechaInicio
                    )}
                  </span>
                  <span>-</span>
                  <span>
                    {formatearFechaSimple(
                      (minuta as any).fecha_fin || minuta.fechaFin
                    )}
                  </span>
                </div>
                <div className="flex items-center space-x-1">
                  <ChefHat className="h-4 w-4" />
                  {/* Fix tanda2 T2-07 (2026-06-30): el backend expone
                      el creador como `usuarios` (con "s") no como
                      `creador`. Antes se leía `minuta.creador?.nombre`
                      → undefined → siempre "Usuario desconocido". Ahora
                      probamos ambos por compatibilidad y caemos al
                      fallback solo si NADA hay. */}
                  <span>{minuta.creador?.nombre || (minuta as unknown as { usuarios?: { nombre?: string } }).usuarios?.nombre || 'Usuario desconocido'}</span>
                </div>
              </div>

              {/* Estadísticas en grid */}
              <div className="grid grid-cols-3 gap-4 mb-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                    {(() => {
                      const estadisticasCorrectas =
                        calcularEstadisticasCorrectas(minuta);
                      return (
                        estadisticasCorrectas?.completitudPorcentaje ||
                        estadisticasEnTiempoReal?.completitudPorcentaje ||
                        minuta.estadisticas?.completitudPorcentaje ||
                        0
                      );
                    })()}
                    %
                  </div>
                  <div className="text-xs text-gray-500">Completitud</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600 dark:text-green-400">
                    {estadisticasEnTiempoReal?.totalRecetas ||
                      minuta.estadisticas?.totalRecetas ||
                      0}
                  </div>
                  <div className="text-xs text-gray-500">Recetas</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
                    {formatearMonto(minuta.estadisticas?.costPromedioDia || 0)}
                  </div>
                  <div className="text-xs text-gray-500">Costo/Día</div>
                </div>
              </div>

              {/* Barra de progreso. Numerador y denominador EXACTAMENTE
                  los mismos que el % de arriba: completas / total absoluto.
                  "completas" incluye recetas + "No aplica" (definición
                  Jorge 2026-06-16). El número "83 Recetas" del grid de
                  arriba queda como conteo informativo separado. */}
              {(() => {
                const stats = calcularEstadisticasCorrectas(minuta);
                const num = stats?.celdasCompletas ?? 0;
                const den = stats?.totalAbsoluto ?? 0;
                return (
              <div className="mb-4">
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-500">Completitud</span>
                  <span className="text-xs text-gray-500">
                    {num}
                    /
                    {den}{' '}
                    casillas
                  </span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
                  <div
                    className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    style={{
                      width: `${
                        stats?.completitudPorcentaje ||
                        estadisticasEnTiempoReal?.completitudPorcentaje ||
                        minuta.estadisticas?.completitudPorcentaje ||
                        0
                      }%`,
                    }}
                  ></div>
                </div>
              </div>
                );
              })()}

              {/* Botones de acción */}
              <div className="flex justify-between">
                <button
                  onClick={() =>
                    navigate(`/minutas-nuevas/${minuta.id}/editar`)
                  }
                  className="px-3 py-2 bg-green-100 text-green-700 rounded-lg hover:bg-green-200 dark:bg-green-900 dark:text-green-300 flex items-center space-x-2"
                >
                  <Edit3 className="h-4 w-4" />
                  <span>Editar Minuta</span>
                </button>
                <div className="flex space-x-2">
                  {/* Botón Eliminar Minuta */}
                  <button
                    onClick={() => eliminarMinuta(minuta.id)}
                    className="px-3 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 dark:bg-red-900 dark:text-red-300 dark:hover:bg-red-800 flex items-center space-x-2 text-sm"
                    title="Eliminar minuta"
                  >
                    <Trash2 className="h-4 w-4" />
                    <span>Eliminar</span>
                  </button>

                  {/* Botón Publicar/Bajar Minuta */}
                  {minuta.estado === 'activa' ? (
                    // Botón Bajar (cuando está publicada)
                    <button
                      onClick={() => bajarMinuta(minuta.id)}
                      className="px-3 py-2 bg-orange-100 text-orange-700 rounded-lg hover:bg-orange-200 dark:bg-orange-900 dark:text-orange-300 dark:hover:bg-orange-800 flex items-center space-x-2 text-sm transition-all"
                      title="Bajar minuta a borrador"
                    >
                      <Download className="h-4 w-4" />
                      <span>Bajar</span>
                    </button>
                  ) : (
                    // Botón Publicar (cuando está en borrador).
                    // Se BLOQUEA si quedan celdas sin decidir (sin receta y
                    // sin "No aplica"). El usuario debe completarlas o
                    // marcarlas como "No aplica" antes de publicar.
                    <button
                      onClick={() => {
                        const estadisticasCorrectas =
                          calcularEstadisticasCorrectas(minuta);
                        const totales =
                          estadisticasCorrectas?.totalCeldasPosibles ??
                          (minuta.estadisticas as any)?.celdasTotales ??
                          0;
                        const conReceta =
                          estadisticasCorrectas?.totalRecetas ??
                          minuta.estadisticas?.totalRecetas ??
                          0;
                        const omitidas =
                          estadisticasCorrectas?.celdasOmitidas ??
                          (minuta.estadisticas as any)?.celdasOmitidas ??
                          0;
                        const faltantes = Math.max(
                          totales - conReceta - omitidas,
                          0
                        );
                        publicarMinuta(minuta.id, faltantes);
                      }}
                      className="px-3 py-2 rounded-lg flex items-center space-x-2 text-sm transition-all bg-blue-100 text-blue-700 hover:bg-blue-200 dark:bg-blue-900 dark:text-blue-300 dark:hover:bg-blue-800"
                      title={(() => {
                        const estadisticasCorrectas =
                          calcularEstadisticasCorrectas(minuta);
                        const porcentaje =
                          estadisticasCorrectas?.completitudPorcentaje ||
                          estadisticasEnTiempoReal?.completitudPorcentaje ||
                          minuta.estadisticas?.completitudPorcentaje ||
                          0;
                        return porcentaje === 100
                          ? 'Publicar minuta'
                          : `Publicar minuta — faltan celdas por decidir (${porcentaje}% completa)`;
                      })()}
                    >
                      <Upload className="h-4 w-4" />
                      <span>Publicar</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Acordeón "Buscar minutas anteriores".
            Tarjetas compactas con sólo "Editar" — las acciones
            destructivas (publicar/bajar/eliminar) viven en la vista
            principal o dentro del editor, para evitar accidentes sobre
            minutas viejas. */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <BuscadorMinutasHistoricasAcordeon
            hook={historicasHook}
            idsExcluir={new Set(minutas.map((m: any) => m.id))}
            descripcion="Busca minutas anteriores por rango de fecha de inicio. Muestra una vista resumida: para operar sobre una, abrila con Editar."
            renderResultados={(resultados) => (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {resultados.map((minuta: any) => {
                  const inicio = minuta.fecha_inicio
                    ? new Date(minuta.fecha_inicio)
                        .toISOString()
                        .slice(0, 10)
                    : '';
                  const fin = minuta.fecha_fin
                    ? new Date(minuta.fecha_fin).toISOString().slice(0, 10)
                    : '';
                  return (
                    <div
                      key={minuta.id}
                      className="border border-gray-200 dark:border-gray-700 rounded-lg p-3 flex items-center justify-between gap-3 bg-gray-50 dark:bg-gray-900/40"
                    >
                      <div className="min-w-0">
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                          {minuta.nombre}
                        </div>
                        <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                          {inicio} → {fin}
                          {minuta.estado && (
                            <span
                              className={`ml-2 px-1.5 py-0.5 rounded text-[10px] uppercase tracking-wide ${obtenerColorEstado(minuta.estado)}`}
                            >
                              {minuta.estado}
                            </span>
                          )}
                          {typeof minuta.estadisticas
                            ?.completitudPorcentaje === 'number' && (
                            <span className="ml-2">
                              {minuta.estadisticas.completitudPorcentaje}%
                            </span>
                          )}
                        </div>
                      </div>
                      <button
                        type="button"
                        onClick={() =>
                          navigate(`/minutas-nuevas/${minuta.id}/editar`)
                        }
                        className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-1 flex-shrink-0"
                        title="Abrir editor"
                      >
                        <Edit3 className="w-3.5 h-3.5" />
                        Editar
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          />
        </div>
      </div>
    );
  };

  // ============================================================================
  // VISTA EXCEL (TABLA 7x10)
  // ============================================================================

  const VistaExcel = () => {
    if (!vistaExcel) return <div>Cargando...</div>;

    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <div className="flex items-center space-x-3">
              <button
                onClick={() => setVista('lista')}
                className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
              >
                <X className="h-5 w-5" />
              </button>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                  {vistaExcel.minuta.nombre}
                </h1>
                <p className="text-gray-600 dark:text-gray-400">
                  Vista detallada tipo Excel •{' '}
                  {estadisticasEnTiempoReal
                    ? estadisticasEnTiempoReal.completitudPorcentaje
                    : vistaExcel.estadisticas.completitudPorcentaje}
                  % completado
                </p>
              </div>
            </div>
          </div>
          <div className="flex space-x-3">
            {/* Mensaje de guardado automático */}
            {modoEdicion && (
              <div className="px-4 py-2 rounded-lg flex items-center space-x-2 bg-blue-100 text-blue-800 border border-blue-300">
                <CheckCircle className="h-4 w-4" />
                <span className="text-sm">Guardado automático activado</span>
              </div>
            )}

            <button
              onClick={() => setModoEdicion(!modoEdicion)}
              className={`px-4 py-2 rounded-lg flex items-center space-x-2 ${
                modoEdicion
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-blue-600 text-white hover:bg-blue-700'
              }`}
            >
              {modoEdicion ? (
                <>
                  <Save className="h-4 w-4" />
                  <span>Guardar</span>
                </>
              ) : (
                <>
                  <Edit3 className="h-4 w-4" />
                  <span>Editar</span>
                </>
              )}
            </button>
          </div>
        </div>

        {/* Estadísticas rápidas */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-blue-600 dark:text-blue-400">
              {estadisticasEnTiempoReal
                ? estadisticasEnTiempoReal.totalRecetas
                : vistaExcel.estadisticas.totalRecetas}
            </div>
            <div className="text-sm text-gray-500">Recetas asignadas</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-green-600 dark:text-green-400">
              {estadisticasEnTiempoReal
                ? estadisticasEnTiempoReal.diasConRecetas
                : vistaExcel.estadisticas.diasCompletos}
            </div>
            <div className="text-sm text-gray-500">Días con recetas</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-purple-600 dark:text-purple-400">
              {estadisticasEnTiempoReal
                ? formatearMonto(estadisticasEnTiempoReal.costoTotal)
                : formatearMonto(0)}
            </div>
            <div className="text-sm text-gray-500">Costo total</div>
          </div>
          <div className="bg-white dark:bg-gray-800 p-4 rounded-lg border border-gray-200 dark:border-gray-700">
            <div className="text-2xl font-bold text-orange-600 dark:text-orange-400">
              {estadisticasEnTiempoReal
                ? estadisticasEnTiempoReal.completitudPorcentaje
                : vistaExcel.estadisticas.completitudPorcentaje}
              %
            </div>
            <div className="text-sm text-gray-500">Completitud</div>
          </div>
        </div>

        {/* Tabla principal */}
        <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider sticky left-0 bg-gray-50 dark:bg-gray-700 z-10">
                    Tipo de Plato
                  </th>
                  {Object.keys(DIAS_SEMANA).map((dia) => (
                    <th
                      key={dia}
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider min-w-[200px]"
                    >
                      <div>
                        {DIAS_SEMANA[parseInt(dia) as keyof typeof DIAS_SEMANA]}
                      </div>
                      <div className="text-xs text-gray-400 font-normal">
                        {new Date(
                          `2025-09-${14 + parseInt(dia)}`
                        ).toLocaleDateString('es-CL', {
                          day: 'numeric',
                          month: 'short',
                        })}
                      </div>
                      <div className="text-sm font-semibold text-green-600 dark:text-green-400 mt-1">
                        Total:{' '}
                        {formatearMonto(calcularTotalPorDia(parseInt(dia)))}
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {tiposPlatos.map((tipoPlato) => (
                  <tr
                    key={tipoPlato.id}
                    className="hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    <td className="px-4 py-4 whitespace-nowrap sticky left-0 bg-white dark:bg-gray-800 z-10 border-r border-gray-200 dark:border-gray-700">
                      <div className="flex items-center space-x-3">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{
                            backgroundColor: tipoPlato.color || '#6B7280',
                          }}
                        ></div>
                        <div>
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {tipoPlato.nombre}
                          </div>
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {tipoPlato.descripcion}
                          </div>
                        </div>
                      </div>
                    </td>
                    {Object.keys(DIAS_SEMANA).map((dia) => {
                      // Verificar si hay receta asignada en nuestro estado local
                      const clavecelda = `${parseInt(dia)}-${tipoPlato.id}`;
                      const recetaLocal = recetasAsignadas[clavecelda];

                      // También verificar si hay receta en vistaExcel (datos del servidor)
                      const recetaServidor =
                        vistaExcel?.semana?.[parseInt(dia)]?.[tipoPlato.id];

                      // Priorizar receta local sobre la del servidor
                      const recetaAsignada = recetaLocal || recetaServidor;
                      return (
                        <td
                          key={`${tipoPlato.id}-${dia}`}
                          className="px-4 py-4"
                        >
                          {recetaAsignada ? (
                            <div className="space-y-2">
                              <div className="text-sm font-medium text-gray-900 dark:text-white">
                                {recetaLocal
                                  ? recetaLocal.nombre
                                  : recetaServidor?.receta?.nombre}
                              </div>
                              <div className="flex justify-between items-center text-xs">
                                <span className="text-gray-500 dark:text-gray-400">
                                  {formatearMonto(
                                    recetaLocal
                                      ? calcularCostoReal(recetaLocal)
                                      : recetaServidor?.receta
                                        ? calcularCostoReal(
                                            recetaServidor.receta
                                          )
                                        : 0
                                  )}
                                </span>
                                {modoEdicion && (
                                  <div className="flex space-x-1">
                                    <button
                                      onClick={() =>
                                        abrirSelectorRecetas(
                                          parseInt(dia),
                                          tipoPlato
                                        )
                                      }
                                      className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                                      title="Cambiar receta"
                                    >
                                      <RefreshCw className="h-3 w-3" />
                                    </button>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        eliminarReceta(
                                          parseInt(dia),
                                          tipoPlato
                                        );
                                      }}
                                      className="p-1 text-red-600 hover:bg-red-100 rounded"
                                      title="Eliminar receta"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                    </button>
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : (
                            <div
                              onClick={() =>
                                modoEdicion &&
                                abrirSelectorRecetas(parseInt(dia), tipoPlato)
                              }
                              className="flex items-center justify-center h-20 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors cursor-pointer"
                            >
                              {modoEdicion ? (
                                <div className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-200 flex items-center justify-center">
                                  <Plus className="h-5 w-5" />
                                </div>
                              ) : (
                                <span className="text-xs text-gray-400">
                                  Sin asignar
                                </span>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MODAL PARA SELECCIONAR RECETAS
  // ============================================================================

  const ModalSelectorRecetas = () => {
    if (!mostrarSelectorRecetas || !celdaSeleccionada) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                      Seleccionar Receta para{' '}
                      {celdaSeleccionada.tipoPlato.nombre}
                    </h3>
                    <button
                      onClick={() => {
                        setMostrarSelectorRecetas(false);
                        setCeldaSeleccionada(null);
                      }}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    >
                      <X className="h-6 w-6" />
                    </button>
                  </div>

                  <div className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                    Día:{' '}
                    {
                      DIAS_SEMANA[
                        celdaSeleccionada.dia as keyof typeof DIAS_SEMANA
                      ]
                    }
                  </div>

                  {cargandoRecetas ? (
                    <div className="flex justify-center items-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-2 text-gray-600 dark:text-gray-400">
                        Cargando recetas...
                      </span>
                    </div>
                  ) : recetasDisponibles.length === 0 ? (
                    <div className="text-center py-8">
                      <ChefHat className="mx-auto h-12 w-12 text-gray-400" />
                      <p className="mt-2 text-gray-600 dark:text-gray-400">
                        No hay recetas disponibles para{' '}
                        {celdaSeleccionada.tipoPlato.nombre.toLowerCase()}
                      </p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 max-h-96 overflow-y-auto">
                      {recetasDisponibles.map((receta) => (
                        <div
                          key={receta.id}
                          className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 hover:bg-gray-100 dark:hover:bg-gray-600 cursor-pointer transition-colors"
                          onClick={() => seleccionarReceta(receta)}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="text-sm font-medium text-gray-900 dark:text-white truncate">
                              {receta.nombre}
                            </h4>
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                              {formatearMonto(calcularCostoReal(receta))}
                            </span>
                          </div>

                          {receta.descripcion && (
                            <p className="text-xs text-gray-600 dark:text-gray-400 mb-2 line-clamp-2">
                              {receta.descripcion}
                            </p>
                          )}

                          <div className="flex justify-between items-center text-xs text-gray-500 dark:text-gray-400">
                            <span>{receta.porciones} porciones</span>
                            <span>{receta.tiempo_preparacion} min</span>
                          </div>

                          <div className="mt-2">
                            <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                              {receta.categoria}
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={() => {
                  setMostrarSelectorRecetas(false);
                  setCeldaSeleccionada(null);
                }}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MODAL PARA CONFIRMAR ELIMINACIÓN DE MINUTA
  // ============================================================================

  const ModalEliminarMinuta = () => {
    if (!mostrarModalEliminar) return null;

    const minutaSeleccionada = minutas.find((m) => m.id === minutaAEliminar);

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <Trash2 className="h-6 w-6 text-red-600" />
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white">
                    Eliminar Minuta
                  </h3>
                  <div className="mt-2">
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      ¿Estás seguro de que deseas eliminar la minuta{' '}
                      <span className="font-semibold text-gray-900 dark:text-white">
                        "{minutaSeleccionada?.nombre}"
                      </span>
                      ? Esta acción no se puede deshacer y se perderán todas las
                      recetas asociadas.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={confirmarEliminarMinuta}
                disabled={cargando}
              >
                {cargando ? 'Eliminando...' : 'Eliminar'}
              </button>
              <button
                type="button"
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                onClick={() => {
                  setMostrarModalEliminar(false);
                  setMinutaAEliminar(null);
                }}
                disabled={cargando}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ============================================================================
  // MODAL PARA CREAR NUEVA MINUTA
  // ============================================================================

  const ModalCrearMinuta = useMemo(() => {
    if (!mostrarModalCrear) return null;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
          <div className="fixed inset-0 transition-opacity" aria-hidden="true">
            <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
          </div>

          <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
            <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
              <div className="sm:flex sm:items-start">
                <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                  <h3 className="text-lg leading-6 font-medium text-gray-900 dark:text-white mb-4">
                    Crear Nueva Minuta
                  </h3>

                  <div className="space-y-4">
                    {/* Fechas primero, para que se genere el nombre */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fecha de Inicio *
                        </label>
                        <input
                          type="date"
                          value={formularioNuevaMinuta.fechaInicio}
                          onChange={handleFechaInicioChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Fecha de Fin *
                        </label>
                        <input
                          type="date"
                          value={formularioNuevaMinuta.fechaFin}
                          onChange={handleFechaFinChange}
                          className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    </div>

                    {/* Nombre (generado automáticamente o personalizado) */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                          Nombre de la Minuta
                        </label>
                        <label className="flex items-center text-xs text-gray-600 dark:text-gray-400">
                          <input
                            type="checkbox"
                            checked={nombrePersonalizado}
                            onChange={handlePersonalizadoChange}
                            className="h-3 w-3 text-blue-600 border-gray-300 rounded mr-1.5"
                          />
                          Personalizar nombre
                        </label>
                      </div>
                      <input
                        key="input-nombre-minuta"
                        type="text"
                        value={formularioNuevaMinuta.nombre}
                        onChange={handleNombreChange}
                        readOnly={!nombrePersonalizado}
                        className={`w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 ${
                          nombrePersonalizado
                            ? 'bg-white dark:bg-gray-700 text-gray-900 dark:text-white'
                            : 'bg-gray-50 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed'
                        }`}
                        placeholder={
                          nombrePersonalizado
                            ? 'Ingresa un nombre personalizado'
                            : 'Selecciona las fechas para generar el nombre automáticamente'
                        }
                      />
                    </div>

                    {/* Descripción */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Descripción (opcional)
                      </label>
                      <textarea
                        value={formularioNuevaMinuta.descripcion}
                        onChange={handleDescripcionChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        rows={3}
                        placeholder="Descripción del menú semanal..."
                      />
                    </div>

                    {/* Estado */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Estado
                      </label>
                      <select
                        value={formularioNuevaMinuta.estado}
                        onChange={handleEstadoChange}
                        className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      >
                        <option value="borrador">Borrador</option>
                        <option value="activa">Activa</option>
                      </select>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
              <button
                type="button"
                onClick={manejarCrearMinuta}
                disabled={
                  cargando ||
                  !formularioNuevaMinuta.nombre ||
                  !formularioNuevaMinuta.fechaInicio ||
                  !formularioNuevaMinuta.fechaFin
                }
                className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {cargando ? 'Creando...' : 'Crear Minuta'}
              </button>
              <button
                type="button"
                onClick={() => setMostrarModalCrear(false)}
                className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }, [
    mostrarModalCrear,
    formularioNuevaMinuta,
    nombrePersonalizado,
    cargando,
    handleNombreChange,
    handleDescripcionChange,
    handleFechaInicioChange,
    handleFechaFinChange,
    handlePersonalizadoChange,
    handleEstadoChange,
    manejarCrearMinuta,
  ]);

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-6">
      <div className="max-w-full mx-auto">
        {vista === 'lista' ? <VistaLista /> : <VistaExcel />}
        {ModalCrearMinuta}
        <ModalEliminarMinuta />
        <ModalSelectorRecetas />

        {/* Modal de confirmación */}
        <ConfirmModal
          isOpen={modalConfirmConfig.isOpen}
          onClose={() =>
            setModalConfirmConfig({ ...modalConfirmConfig, isOpen: false })
          }
          onConfirm={() => {
            modalConfirmConfig.onConfirm();
            setModalConfirmConfig({ ...modalConfirmConfig, isOpen: false });
          }}
          title={modalConfirmConfig.title}
          message={modalConfirmConfig.message}
          type={modalConfirmConfig.type}
          confirmText={modalConfirmConfig.confirmText}
          cancelText={modalConfirmConfig.cancelText}
        />
      </div>
    </div>
  );
};

export default MinutasNuevas;
